/**
 * THE YEARLY SUMMARY PLAQUES: one aggregate card per calendar year, on the chart.
 *
 * <h2>The metrics are NOT computed here</h2>
 *
 * {@link annualBreakdown} already groups a finished ledger by calendar year and produces exactly
 * the three figures asked for — net PnL, the win/loss split, and that year's own max drawdown.
 * It is itself a deliberate second implementation of the engine's `LedgerStatistics`, pinned
 * against it by a test, and this file adding a third would be the drift that file was written to
 * avoid. So this module is PRESENTATION only: it takes those rows and turns them into plaques.
 *
 * <p>Two consequences worth knowing, both inherited and both correct:
 *
 * <ul>
 *   <li>A year is the year of the <b>EXIT</b>, by string slice. A trade opened in December and
 *       closed in January belongs to January's year — the year it was realised in.</li>
 *   <li>Each year's curve restarts at 1.0, so the drawdown is that year's own and the net figures
 *       do not compound across the row. They are per-year readings, not slices of a total.</li>
 * </ul>
 *
 * <h2>How they reach the chart</h2>
 *
 * As ordinary card DESCRIPTORS, in the same list the trade cards travel in. That is the whole
 * injection strategy and it was the point of the previous change: `layoutCards` already places
 * cards at the grid edges and packs them, `cardGraphics` already builds a draggable `graphic`,
 * and `renderTradeCards` already converts data to pixels and re-places everything on zoom. A
 * plaque is a card with different content, so it needs none of that again — and because it goes
 * through the SAME packer, a plaque and a trade card cannot be laid on top of each other.
 *
 * <p>Two things a plaque does differently, both carried as flags on the descriptor rather than as
 * a second code path: it anchors to the TOP MARGIN instead of to a price, and it draws no leader
 * line. There is no marker for it to point at — it describes a year, not a trade.
 */

import { annualBreakdown } from './annualBreakdown';
import { CARD_SIDE, DECORATOR_TYPE } from './tradeDecorator';

/** The anchor date for a year's plaque: mid-year, so it sits over the year it describes. */
export const SUMMARY_ANCHOR_MONTH_DAY = '-07-01';

/**
 * A PLAQUE IS NOT A TRADE CARD, and the palette says so before the text does.
 *
 * <h3>CONTRAST INVERSION: a light object among dark ones</h3>
 *
 * Every other thing on this canvas is dark — the candles, the trade cards' navy and charcoal, the
 * chart's own ground. The plaque used to be dark too, so it was differentiated only by a gold
 * border and a shadow: the same CLASS of object, slightly dressed up. Inverting it is what makes
 * it read as a different kind of thing, and the inversion does the work the previous border and
 * shadow were being asked to do on their own.
 *
 * <p>SOLID GOLD, not platinum or white, for two reasons. Gold was already the plaque's accent, so
 * the ground is the existing design language turned up rather than a fourth colour introduced —
 * and a white or platinum plaque on a dark chart reads as a browser dialog that failed to style,
 * which is precisely the wrong association for the one object meant to look authored.
 *
 * <p>Contrast computed rather than eyeballed, by WCAG relative luminance: deep black on
 * {@code #D4A017} is <b>8.8:1</b>, comfortably past the 7:1 AAA threshold at any size. That
 * matters here more than usual — these are screenshots, and a screenshot is often read scaled
 * down in a deck or printed in greyscale, where gold and black survive and a mid-tone pair does
 * not.
 */
export const SUMMARY_THEME = {
  /** Solid institutional gold. The whole point: the only light object on the canvas. */
  background: '#D4A017',
  /**
   * A BLACK frame, not a gold one. On a gold ground a gold border is invisible, and the border is
   * what gives the plaque a hard printed edge against the price action behind it.
   */
  border: '#0A0A0A',
  /**
   * Stark deep black. {@code #0A0A0A} rather than {@code #000}: pure black beside saturated gold
   * fringes on a subpixel-rendered canvas, and this is type that will be read at 16px.
   */
  text: '#0A0A0A',
  /**
   * The labels, subordinate but still legible — a dark bronze that belongs to the gold rather
   * than a grey laid over it. 3.7:1 against the ground, which is the AA bar for type this size.
   */
  muted: '#6B5207',
  /** The year header is the same deep black as the values. On gold it needs no second colour. */
  header: '#0A0A0A',
};

/** Heavier again. A 3px black frame is what stops a light plaque bleeding into a pale candle. */
export const SUMMARY_BORDER_WIDTH = 3;

/**
 * IT FLOATS, and now it has to.
 *
 * <p>A dark plaque on a dark chart needed a shadow to separate it. A LIGHT plaque needs one for a
 * different reason: it is the brightest object on the canvas, and without a shadow it reads as a
 * hole cut in the chart rather than a card lying on top of it. 25px at 65% is deep enough to
 * carry that at the sizes below.
 *
 * <p>Still deliberately NOT given to the trade cards: a shadow under each of 516 of them is a
 * grey wash, and part of what makes this the most important object on the chart is being the only
 * one wearing one.
 */
export const SUMMARY_SHADOW = { blur: 25, color: 'rgba(0,0,0,0.65)', offsetY: 6 };

/** Boardroom-roomy. Vertical 20, horizontal 30 — wider than tall, so the year header breathes. */
export const SUMMARY_PADDING = [20, 30, 20, 30];

/**
 * The plaque's rich styles — a MASSIVE step up from a trade card's 10px.
 *
 * <p>This was once pinned to the cards' 10px with a note saying a larger header would break the
 * layout, and it would have: {@code cardBox} multiplied the longest line by ONE character width,
 * which is only exact while every line is the same size. It now measures each SEGMENT at its own
 * style's size, so a 28px header above 16px rows is measured exactly and the packer keeps its
 * neighbours clear of the real box. That generalisation is what makes this size safe, and it is
 * the reason the type could not simply have been turned up earlier.
 *
 * <p>Sizes verified rather than assumed: a monospaced advance is {@code 0.5498 x fontSize} at
 * every size and weight measured (10-28px, 400/600/700), to four decimals — so the arithmetic
 * holds at the header's 28px exactly as it did at 15px.
 */
export const SUMMARY_TYPE = {
  header: { fontSize: 28, lineHeight: 38 },
  label: { fontSize: 16, lineHeight: 24 },
  value: { fontSize: 16, lineHeight: 24 },
};

export const summaryRich = () => {
  const face = { fontFamily: DECORATOR_TYPE.fontFamily };
  return {
    h: { ...face, ...SUMMARY_TYPE.header, color: SUMMARY_THEME.header, fontWeight: 700 },
    k: { ...face, ...SUMMARY_TYPE.label, color: SUMMARY_THEME.muted },
    v: { ...face, ...SUMMARY_TYPE.value, color: SUMMARY_THEME.text, fontWeight: 600 },
    // THE ACCENTS ARE DARKENED FOR THE LIGHT GROUND. The trade cards' sea green and bright red
    // were chosen against near-black; on gold the red in particular is close to illegible and
    // reads as a warning badge. These are the same two readings at boardroom weight -- deep
    // forest and deep crimson, 6.1:1 and 5.7:1 against the ground.
    win: { ...face, ...SUMMARY_TYPE.value, color: '#14532D', fontWeight: 700 },
    loss: { ...face, ...SUMMARY_TYPE.value, color: '#7F1D1D', fontWeight: 700 },
  };
};

/** Signed, two decimals — the same convention the trade cards' return uses. */
const pct = (v) => `${v >= 0 ? '+' : ''}${(Number.isFinite(v) ? v : 0).toFixed(2)}%`;

/** Labels padded to a fixed column, which is free on a monospaced face and reads as a table. */
const row = (label, value) => `${label.padEnd(7)}${value}`;

/**
 * One plaque's text.
 *
 * <p>NET takes the win/loss accent and nothing else does. The drawdown is always a loss and
 * colouring it red would say nothing; the W/L split is a count, not an outcome.
 */
export const summaryText = (r) => {
  const tone = !Number.isFinite(r.netPnlPercent) || r.netPnlPercent === 0
    ? 'v'
    : (r.netPnlPercent > 0 ? 'win' : 'loss');
  return [
    `{h|[${r.year}] PERFORMANCE}`,
    `{k|${row('NET', '')}}{${tone}|${pct(r.netPnlPercent)}}`,
    `{k|${row('W/L', '')}}{v|${r.wins}W | ${r.losses}L}`,
    `{k|${row('MAX DD', '')}}{v|${(Number.isFinite(r.maxDrawdownPercent)
      ? r.maxDrawdownPercent : 0).toFixed(2)}%}`,
  ].join('\n');
};

/**
 * The bar index a year's plaque hangs over: the first session on or after 1 July.
 *
 * <p>ON OR AFTER, because 1 July is a trading day in roughly five years out of seven and the
 * plaque has to land somewhere in all of them. A plain lookup would silently drop every year
 * whose July opened on a weekend.
 *
 * <p>A plain string comparison, not a Date: `barData.dates` are `YYYY-MM-DD` and sorted, so they
 * compare lexicographically in date order. Parsing them would reintroduce the timezone shift this
 * repo has been caught by before, to answer a question that needs no clock.
 *
 * <p>Falls back to the LAST session of that year — a year that ended before July (the first or
 * last year of a series usually does) still gets its plaque, rather than vanishing because the
 * calendar ran out.
 */
export const summaryAnchorIndex = (dates, year) => {
  if (!Array.isArray(dates) || dates.length === 0) return null;
  const target = `${year}${SUMMARY_ANCHOR_MONTH_DAY}`;
  let lastInYear = null;
  for (let i = 0; i < dates.length; i += 1) {
    const d = dates[i];
    if (typeof d !== 'string' || d.slice(0, 4) !== String(year)) continue;
    lastInYear = i;
    if (d >= target) return i;
  }
  return lastInYear;
};

/**
 * How many years the drawn trades span, for the Admin Panel's label.
 *
 * <p>Derived from the overlays rather than tracked beside them, for the same reason
 * `tradeCardKeys` is: a separately maintained count goes stale the moment a run is repeated, and
 * the symptom is a button that offers more plaques than appear.
 */
export const yearsOnChart = (overlays) => {
  const years = new Set();
  (Array.isArray(overlays) ? overlays : []).forEach((o) => {
    (o && Array.isArray(o.trades) ? o.trades : []).forEach((t) => {
      if (!t || t.open || typeof t.exitTime !== 'string') return;
      const y = t.exitTime.slice(0, 4);
      if (/^\d{4}$/.test(y)) years.add(y);
    });
  });
  return years.size;
};

/**
 * THE PLAQUES, as card descriptors.
 *
 * @param trades the trades DRAWN on the chart — see the note at the call site about why it is the
 *               drawn set rather than the run's full list
 * @param dates  the chart's own `barData.dates`
 */
export const yearlySummaryCards = (trades, dates) => {
  const { rows } = annualBreakdown(trades);
  const styles = summaryRich();
  return rows.map((r) => {
    const index = summaryAnchorIndex(dates, r.year);
    if (index === null) return null;
    return {
      key: `year:${r.year}`,
      // Marks it as an aggregate everywhere downstream -- the ledger, the audit and any future
      // reader of the descriptor -- rather than leaving `key.startsWith('year:')` as the test.
      kind: 'summary',
      year: r.year,
      index,
      // ANCHORED TO THE TOP MARGIN, not to a price. A year has no price, and picking one (the
      // year's high? its mid?) would be inventing a level and then drawing a line to it.
      anchorTop: true,
      leader: false,
      // Unused for an anchorTop card, but `convertToPixel` wants a pair.
      price: 0,
      side: CARD_SIDE.ABOVE,
      rich: summaryText(r),
      richStyles: styles,
      theme: SUMMARY_THEME,
      borderWidth: SUMMARY_BORDER_WIDTH,
      padding: SUMMARY_PADDING,
      shadowBlur: SUMMARY_SHADOW.blur,
      shadowColor: SUMMARY_SHADOW.color,
      shadowOffsetY: SUMMARY_SHADOW.offsetY,
    };
  }).filter(Boolean);
};
